import { useEffect, useState } from "react";
import {
  UsersThree, Plus, PencilSimple, Trash, GraduationCap, Paperclip, Buildings, Clock,
} from "@phosphor-icons/react";
import { Loader2, Upload } from "lucide-react";
import { api, apiError, fileUrl } from "@/lib/api";
import { useTenant } from "@/context/TenantContext";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PageHero } from "@/components/PageHero";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { Checkbox } from "@/components/ui/checkbox";
import {
  TIPI_ATTIVITA_FORMATIVA, DESTINATARI_FORMAZIONE, METODI_VERIFICA_EFFICACIA, ESITI_VERIFICA,
} from "@/lib/constants";
import { toast } from "sonner";

const emptyEmp = () => ({
  tenant_id: "", nome: "", ruolo: "", reparto: "", data_assunzione: "",
  email: "", telefono: "", competenze: [], note: "", formazioni: [],
});
const emptyTraining = () => ({
  titolo: "", esempi: "", data: "", ore: "", scadenza: "", ente: "", docente: "", docente_tipo: "interno",
  pianificata: true, finalita: "", tipo_attivita: [], destinatari: [], riferimenti: "",
  verifica_efficacia: [], esito_verifica: "", vnc_numero: "", note: "", attestato_url: null,
});

const trainingBadge = (scadenza) => {
  if (!scadenza) return null;
  const days = Math.ceil((new Date(scadenza + "T00:00:00") - new Date()) / 86400000);
  if (days < 0) return <Badge className="bg-red-100 text-red-700 border-red-200">Scaduta</Badge>;
  if (days <= 30) return <Badge className="bg-amber-100 text-amber-800 border-amber-200">Tra {days} gg</Badge>;
  return <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">Valida</Badge>;
};

export default function Employees() {
  const { tenantId, organizations, currentOrg } = useTenant();
  const { user } = useAuth();
  const isConsultant = user?.role === "consultant";
  const canWrite = user?.role === "consultant" || user?.role === "client_user";

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [empOpen, setEmpOpen] = useState(false);
  const [form, setForm] = useState(emptyEmp());
  const [competenzeStr, setCompetenzeStr] = useState("");
  const [editingId, setEditingId] = useState(null);

  const [trainOpen, setTrainOpen] = useState(false);
  const [trainForm, setTrainForm] = useState(emptyTraining());
  const [trainEmp, setTrainEmp] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [openAcc, setOpenAcc] = useState({});

  const load = () => {
    setLoading(true);
    const p = tenantId ? `?tenant_id=${tenantId}` : "";
    api.get(`/employees${p}`).then((r) => setItems(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, [tenantId]);

  const openNew = () => {
    setEditingId(null);
    setForm({ ...emptyEmp(), tenant_id: tenantId || "" });
    setCompetenzeStr("");
    setEmpOpen(true);
  };
  const openEdit = (e) => {
    setEditingId(e.id);
    setForm({ ...emptyEmp(), ...e });
    setCompetenzeStr((e.competenze || []).join(", "));
    setEmpOpen(true);
  };

  const saveEmp = async () => {
    if (!form.tenant_id) { toast.error("Seleziona un cliente"); return; }
    if (!form.nome.trim()) { toast.error("Inserisci il nome"); return; }
    const payload = {
      ...form,
      competenze: competenzeStr.split(",").map((s) => s.trim()).filter(Boolean),
      formazioni: (form.formazioni || []).map((f) => ({ ...f, ore: f.ore === "" || f.ore == null ? null : Number(f.ore) })),
    };
    try {
      if (editingId) { await api.put(`/employees/${editingId}`, payload); toast.success("Dipendente aggiornato"); }
      else { await api.post("/employees", payload); toast.success("Dipendente creato"); }
      setEmpOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const removeEmp = async (id) => {
    if (!window.confirm("Eliminare questo dipendente e le sue formazioni?")) return;
    try { await api.delete(`/employees/${id}`); toast.success("Dipendente eliminato"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const openTraining = (emp) => {
    setTrainEmp(emp);
    setTrainForm(emptyTraining());
    setOpenAcc((o) => ({ ...o, [emp.id]: true }));
    setTrainOpen(true);
  };

  const uploadAttestato = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("tenant_id", trainEmp.tenant_id);
      fd.append("file", file);
      const r = await api.post("/employee-file", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setTrainForm((t) => ({ ...t, attestato_url: r.data.storage_path }));
      toast.success("Attestato caricato");
    } catch (e) { toast.error(apiError(e)); }
    finally { setUploading(false); }
  };

  const toggleTrainArr = (key, val) => setTrainForm((t) => {
    const arr = t[key] || [];
    return { ...t, [key]: arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val] };
  });

  const saveTraining = async () => {
    if (!trainForm.titolo.trim()) { toast.error("Inserisci l'oggetto dell'attività formativa"); return; }
    const emp = trainEmp;
    const formazioni = [...(emp.formazioni || []), { ...trainForm, ore: trainForm.ore === "" ? null : Number(trainForm.ore) }];
    const payload = { ...emptyEmp(), ...emp, competenze: emp.competenze || [], formazioni };
    delete payload.id; delete payload.created_at;
    try {
      await api.put(`/employees/${emp.id}`, payload);
      toast.success("Formazione aggiunta"); setTrainOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const removeTraining = async (emp, idx) => {
    const formazioni = (emp.formazioni || []).filter((_, i) => i !== idx);
    const payload = { ...emptyEmp(), ...emp, competenze: emp.competenze || [], formazioni };
    delete payload.id; delete payload.created_at;
    try { await api.put(`/employees/${emp.id}`, payload); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const orgName = (tid) => organizations.find((o) => o.id === tid)?.nome || "";

  return (
    <div className="space-y-6">
      <PageHero label="Personale" title="Dipendenti & formazione"
        subtitle={tenantId ? currentOrg?.nome : "Anagrafica del personale e registro delle formazioni e qualifiche"}>
        {canWrite && (
          <Button onClick={openNew} data-testid="new-employee-btn"
            className="bg-gradient-to-r from-blue-600 to-indigo-500 hover:from-blue-700 hover:to-indigo-600 text-white">
            <Plus className="h-4 w-4 mr-2" /> Nuovo dipendente
          </Button>
        )}
      </PageHero>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : items.length === 0 ? (
        <Card className="p-12 text-center border-slate-200">
          <UsersThree size={40} className="text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">Nessun dipendente registrato{tenantId ? "" : ". Seleziona un cliente per aggiungerne"}.</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="employees-list">
          {items.map((e) => (
            <Card key={e.id} data-testid={`employee-${e.id}`} className="p-5 border-slate-200">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="font-semibold text-slate-900 truncate">{e.nome}</h3>
                  <div className="text-sm text-slate-500">{[e.ruolo, e.reparto].filter(Boolean).join(" · ") || "—"}</div>
                  <div className="flex flex-wrap gap-2 mt-2 text-xs text-slate-500">
                    {!tenantId && isConsultant && <span className="inline-flex items-center gap-1"><Buildings size={13} /> {orgName(e.tenant_id)}</span>}
                    {e.data_assunzione && <span className="inline-flex items-center gap-1"><Clock size={13} /> Assunto il {e.data_assunzione}</span>}
                    {e.email && <span>{e.email}</span>}
                    {e.telefono && <span>{e.telefono}</span>}
                  </div>
                </div>
                {canWrite && (
                  <div className="flex items-center gap-1 shrink-0">
                    <Button size="icon" variant="ghost" className="h-8 w-8" data-testid={`edit-employee-${e.id}`} onClick={() => openEdit(e)}><PencilSimple size={16} /></Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600" data-testid={`delete-employee-${e.id}`} onClick={() => removeEmp(e.id)}><Trash size={16} /></Button>
                  </div>
                )}
              </div>

              {(e.competenze || []).length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {e.competenze.map((c, i) => <Badge key={i} variant="secondary" className="text-xs">{c}</Badge>)}
                </div>
              )}
              {e.note && <p className="text-xs text-slate-500 mt-2 italic">{e.note}</p>}

              <Accordion type="single" collapsible className="mt-3"
                value={openAcc[e.id] ? "formazioni" : ""}
                onValueChange={(v) => setOpenAcc((o) => ({ ...o, [e.id]: v === "formazioni" }))}>
                <AccordionItem value="formazioni" className="border-t border-slate-100">
                  <AccordionTrigger className="text-sm py-2 hover:no-underline" data-testid={`trainings-toggle-${e.id}`}>
                    <span className="inline-flex items-center gap-2"><GraduationCap size={16} className="text-indigo-500" /> Formazioni ({(e.formazioni || []).length})</span>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-2">
                      {(e.formazioni || []).length === 0 && <p className="text-xs text-slate-400 py-1">Nessuna formazione registrata.</p>}
                      {(e.formazioni || []).map((f, i) => (
                        <div key={i} className="flex items-start justify-between gap-2 rounded-md bg-slate-50 p-2.5">
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-slate-800">{f.titolo}</div>
                            {(f.tipo_attivita || []).length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-1">
                                {f.tipo_attivita.map((t, k) => <Badge key={k} variant="secondary" className="text-[10px]">{t}</Badge>)}
                              </div>
                            )}
                            <div className="text-xs text-slate-500 mt-1">{[
                              f.docente && `Docente: ${f.docente}${f.docente_tipo ? ` (${f.docente_tipo})` : ""}`,
                              f.ente,
                              f.data && `svolta ${f.data}`,
                              f.ore != null && f.ore !== "" && `${f.ore}h`,
                              f.scadenza && `scad. ${f.scadenza}`,
                            ].filter(Boolean).join(" · ")}</div>
                            <div className="flex flex-wrap items-center gap-2 mt-1">
                              {f.pianificata === false && <Badge variant="outline" className="text-[10px]">Non pianificata</Badge>}
                              {f.esito_verifica === "SI" && <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px]">Verifica: idoneo</Badge>}
                              {f.esito_verifica === "NO" && <Badge className="bg-rose-100 text-rose-700 border-rose-200 text-[10px]">Verifica: non idoneo</Badge>}
                              {f.esito_verifica === "VNC" && <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-[10px]">VNC{f.vnc_numero ? ` n° ${f.vnc_numero}` : ""}</Badge>}
                              {f.attestato_url && (
                                <a href={fileUrl(f.attestato_url)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:underline">
                                  <Paperclip size={12} /> Attestato
                                </a>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            {trainingBadge(f.scadenza)}
                            {canWrite && <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600" onClick={() => removeTraining(e, i)}><Trash size={14} /></Button>}
                          </div>
                        </div>
                      ))}
                      {canWrite && (
                        <Button size="sm" variant="outline" className="w-full mt-1" data-testid={`add-training-${e.id}`} onClick={() => openTraining(e)}>
                          <Plus className="h-3.5 w-3.5 mr-1.5" /> Aggiungi formazione
                        </Button>
                      )}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </Card>
          ))}
        </div>
      )}

      {/* Employee dialog */}
      <Dialog open={empOpen} onOpenChange={setEmpOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editingId ? "Modifica dipendente" : "Nuovo dipendente"}</DialogTitle></DialogHeader>
          <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-1">
            {!editingId && (
              <div>
                <Label>Cliente</Label>
                <Select value={form.tenant_id} onValueChange={(v) => setForm({ ...form, tenant_id: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="employee-tenant"><SelectValue placeholder="Seleziona cliente" /></SelectTrigger>
                  <SelectContent>{organizations.map((o) => <SelectItem key={o.id} value={o.id}>{o.nome}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div><Label>Nome e cognome</Label><Input value={form.nome} data-testid="employee-nome" onChange={(e) => setForm({ ...form, nome: e.target.value })} className="mt-1.5" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Ruolo</Label><Input value={form.ruolo} data-testid="employee-ruolo" onChange={(e) => setForm({ ...form, ruolo: e.target.value })} className="mt-1.5" /></div>
              <div><Label>Reparto</Label><Input value={form.reparto} onChange={(e) => setForm({ ...form, reparto: e.target.value })} className="mt-1.5" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Data assunzione</Label><Input type="date" value={form.data_assunzione || ""} onChange={(e) => setForm({ ...form, data_assunzione: e.target.value })} className="mt-1.5" /></div>
              <div><Label>Telefono</Label><Input value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} className="mt-1.5" /></div>
            </div>
            <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" /></div>
            <div><Label>Competenze (separate da virgola)</Label><Input value={competenzeStr} data-testid="employee-competenze" onChange={(e) => setCompetenzeStr(e.target.value)} className="mt-1.5" placeholder="es. RSPP, Auditor interno, Primo soccorso" /></div>
            <div><Label>Note</Label><Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="mt-1.5" rows={2} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmpOpen(false)}>Annulla</Button>
            <Button onClick={saveEmp} data-testid="save-employee-btn" className="bg-gradient-to-r from-blue-600 to-indigo-500 hover:from-blue-700 hover:to-indigo-600 text-white">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Training dialog — Scheda Attività Formativa */}
      <Dialog open={trainOpen} onOpenChange={setTrainOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Scheda attività formativa — {trainEmp?.nome}</DialogTitle></DialogHeader>
          <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
            <div>
              <Label>Oggetto dell'attività formativa</Label>
              <Input value={trainForm.titolo} data-testid="training-titolo" onChange={(e) => setTrainForm({ ...trainForm, titolo: e.target.value })} className="mt-1.5" placeholder="es. Utilizzo in sicurezza del carrello elevatore" />
            </div>
            <div>
              <Label>Esempi / contenuti utilizzati</Label>
              <Textarea value={trainForm.esempi} onChange={(e) => setTrainForm({ ...trainForm, esempi: e.target.value })} className="mt-1.5" rows={2} />
            </div>

            <div>
              <Label>Tipo di attività</Label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2">
                {TIPI_ATTIVITA_FORMATIVA.map((t) => (
                  <label key={t} className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox checked={(trainForm.tipo_attivita || []).includes(t)} onCheckedChange={() => toggleTrainArr("tipo_attivita", t)} />
                    {t}
                  </label>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Destinatari</Label>
                <div className="flex flex-col gap-2 mt-2">
                  {DESTINATARI_FORMAZIONE.map((d) => (
                    <label key={d} className="flex items-center gap-2 text-sm cursor-pointer">
                      <Checkbox checked={(trainForm.destinatari || []).includes(d)} onCheckedChange={() => toggleTrainArr("destinatari", d)} />
                      {d}
                    </label>
                  ))}
                </div>
              </div>
              <div className="space-y-3">
                <div>
                  <Label>Pianificazione</Label>
                  <Select value={trainForm.pianificata ? "si" : "no"} onValueChange={(v) => setTrainForm({ ...trainForm, pianificata: v === "si" })}>
                    <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="si">Pianificata</SelectItem>
                      <SelectItem value="no">Non pianificata</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Finalità</Label>
                  <Input value={trainForm.finalita} onChange={(e) => setTrainForm({ ...trainForm, finalita: e.target.value })} className="mt-1.5" placeholder="es. Sensibilizzazione" />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div><Label>Docente</Label><Input value={trainForm.docente} onChange={(e) => setTrainForm({ ...trainForm, docente: e.target.value })} className="mt-1.5" /></div>
              <div>
                <Label>Tipo docente</Label>
                <Select value={trainForm.docente_tipo} onValueChange={(v) => setTrainForm({ ...trainForm, docente_tipo: v })}>
                  <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="interno">Interno</SelectItem>
                    <SelectItem value="esterno">Esterno</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Ente erogatore</Label><Input value={trainForm.ente} onChange={(e) => setTrainForm({ ...trainForm, ente: e.target.value })} className="mt-1.5" /></div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div><Label>Data svolgimento</Label><Input type="date" value={trainForm.data} onChange={(e) => setTrainForm({ ...trainForm, data: e.target.value })} className="mt-1.5" /></div>
              <div><Label>Durata (ore)</Label><Input type="number" min="0" step="0.5" value={trainForm.ore} onChange={(e) => setTrainForm({ ...trainForm, ore: e.target.value })} className="mt-1.5" /></div>
              <div><Label>Scadenza</Label><Input type="date" value={trainForm.scadenza} data-testid="training-scadenza" onChange={(e) => setTrainForm({ ...trainForm, scadenza: e.target.value })} className="mt-1.5" /></div>
            </div>

            <div>
              <Label>Riferimenti / documentazione utilizzata come testo</Label>
              <Textarea value={trainForm.riferimenti} onChange={(e) => setTrainForm({ ...trainForm, riferimenti: e.target.value })} className="mt-1.5" rows={2} />
            </div>

            <div className="rounded-lg border border-slate-200 p-3 space-y-3">
              <Label>Verifica efficacia attività formativa</Label>
              <div className="flex flex-wrap gap-4">
                {METODI_VERIFICA_EFFICACIA.map((m) => (
                  <label key={m} className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox checked={(trainForm.verifica_efficacia || []).includes(m)} onCheckedChange={() => toggleTrainArr("verifica_efficacia", m)} />
                    {m}
                  </label>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Esito verifica</Label>
                  <Select value={trainForm.esito_verifica || "none"} onValueChange={(v) => setTrainForm({ ...trainForm, esito_verifica: v === "none" ? "" : v })}>
                    <SelectTrigger className="mt-1.5" data-testid="training-esito"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">—</SelectItem>
                      {ESITI_VERIFICA.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {trainForm.esito_verifica === "VNC" && (
                  <div><Label>VNC n°</Label><Input value={trainForm.vnc_numero} onChange={(e) => setTrainForm({ ...trainForm, vnc_numero: e.target.value })} className="mt-1.5" /></div>
                )}
              </div>
            </div>

            <div><Label>Note</Label><Textarea value={trainForm.note} onChange={(e) => setTrainForm({ ...trainForm, note: e.target.value })} className="mt-1.5" rows={2} /></div>

            <div>
              <Label>Attestato (PDF, opzionale)</Label>
              <div className="mt-1.5 flex items-center gap-2">
                <label className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-slate-200 text-sm cursor-pointer hover:bg-slate-50" data-testid="training-upload">
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                  {trainForm.attestato_url ? "Sostituisci file" : "Carica file"}
                  <input type="file" accept=".pdf,.png,.jpg,.jpeg" className="hidden" onChange={(e) => uploadAttestato(e.target.files?.[0])} />
                </label>
                {trainForm.attestato_url && <span className="text-xs text-emerald-600 inline-flex items-center gap-1"><Paperclip size={12} /> Caricato</span>}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTrainOpen(false)}>Annulla</Button>
            <Button onClick={saveTraining} disabled={uploading} data-testid="save-training-btn" className="bg-gradient-to-r from-blue-600 to-indigo-500 hover:from-blue-700 hover:to-indigo-600 text-white">Salva scheda</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
